"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ToastProvider = void 0;
var _drywall = require("drywall");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _ToastProviderMixin = require("../../mixins/components/ToastProviderMixin");
/**
 * `ToastProvider` manages the state for all `Toast` components
 * in the application. A `Toast` component is not added to the
 * page itself, rather it is enqueued and displayed by the provider. A provider is necessary to make
 * sure that toasts from different parts of the application do not conflict with each other.
 *
 * `ToastProvider` uses [React context](https://reactjs.org/docs/context.html) to manage application
 * state. Wrapping your application with `ToastProvider` works similarly to how
 * [`Context.Provider`](https://reactjs.org/docs/context.html#contextprovider) sets up context
 * scope. Wrapping your application with a toast provider allows descendants of the provider to
 * enqueue toasts.
 *
 * To enqueue a toast, a descendant of `ToastProvider` must consume the toast provider context.
 * To do this, we provide a `withToast` higher order component or a `useToast` hook. When you wrap
 * your component with `withToast`, your component will be enhanced with the `enqueueToast` prop.
 * Similarly when using the `useToast` [hook](https://reactjs.org/docs/hooks-intro.html), the
 * `enqueueToast` function can be accessed from the returned `ToastProvider` context. Calling
 * `enqueueToast` with a `Toast` component will add the toast
 * to the providers queue.
 *
 * It is common to have multiple consumers of `withToast` or `useToast` within your application, but
 * you should generally only have one `ToastProvider`.
 *
 * `ToastProvider` extends the
 * [drywall `ToastProvider`](https://zillow.github.io/drywall/#toastprovider).
 *
 * @since 7.4.0
 */
var ToastProvider = /*#__PURE__*/(0, _styledComponents.default)(function (props) {
  return /*#__PURE__*/_react.default.createElement(_drywall.ToastProvider, props);
}).withConfig({
  displayName: "ToastProvider",
  componentId: "c11n-8-107-0__sc-gj9lyj-0"
})(["", ""], _ToastProviderMixin.ToastProviderMixin);
exports.ToastProvider = ToastProvider;
ToastProvider.displayName = 'ToastProvider';
ToastProvider.propTypes = {
  /**
   * Tells assistive technologies that this is a region where content will be
   * dynamically updated. All of our toast variants use `role="status"` so we
   * default `aria-live` to the less-intrusive "polite" value.
   */
  'aria-live': _propTypes.default.oneOf(['assertive', 'polite', 'off']),
  /**
   * Components that require the `withToast` context need to be children of this component.
   */
  'children': _propTypes.default.node.isRequired,
  /**
   * The default duration in milliseconds to show a toast before automatically dismissing it.
   * Set this to `0` and toasts must manually be dismissed.
   *
   * Note: A `duration` returned by `optionsFn` would take precedence over this.
   */
  'duration': _propTypes.default.number,
  /**
   * The maximum number of toasts visible at once.
   * Set this to `0` to remove the size limit.
   */
  'maxSize': _propTypes.default.number,
  /**
   * An options function allows you to globally customize the options object passed to
   * `enqueueToast`. This is useful for setting a different duration based on other props such as
   * `appearance`.
   *
   * By default, this will set a duration of 5000 for `"info"` and `"success"` toasts,
   * 10,000 for `"warning"` toasts, and `"error"` toasts must be manually dismissed.
   */
  'optionsFn': _propTypes.default.func,
  /**
   * By default, toasts are paused when the mouse enters them.
   */
  'pauseOnMouseEnter': _propTypes.default.bool,
  /**
   * By default, all toasts are paused when the window loses focus.
   */
  'pauseOnWindowBlur': _propTypes.default.bool,
  /**
   * Where the toast region will exist with respect to the viewport.
   */
  'placement': _propTypes.default.oneOf(['top-start', 'top', 'top-end', 'bottom-start', 'bottom', 'bottom-end']),
  /**
   * By default, toasts will be rendered into a Portal in the document body.
   * With this prop, you can specify a different container,
   * or you can disable the portal to render the content inline by passing false.
   */
  'portal': _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.bool]),
  /**
   * A render function for the toast content. By default, toasts will be rendered into an
   * `AnimatedList`. The render function is passed an object with `toasts` as the only property.
   */
  'portalContent': _propTypes.default.func,
  /**
   * By default, toasts are resumed when the mouse leaves them.
   */
  'resumeOnMouseLeave': _propTypes.default.bool,
  /**
   * By default, all toasts are resumed when the window gains focus.
   */
  'resumeOnWindowFocus': _propTypes.default.bool
};
ToastProvider.defaultProps = {
  'aria-live': 'polite',
  'optionsFn': function optionsFn(toast) {
    var appearance = toast.props.appearance;
    if (appearance === 'error') {
      return {
        duration: 0
      };
    }
    if (appearance === 'warning') {
      return {
        duration: 10000
      };
    }
    return null;
  },
  'placement': 'bottom-end'
};

/** @component */